import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobListItemDTO } from '@/types';
import { ChevronLeft, ChevronRight, Calendar, Info } from 'lucide-react';
import { toISOStringNoMs, isTodayInTimeZone, getNowInCompanyTimeZone } from '@/lib/dateUtils';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';

interface SchedulerAssistantProps {
    companyId: string;
    formStartDate?: string | null; // the string value from the date input
}

export function SchedulerAssistant({ companyId, formStartDate }: SchedulerAssistantProps) {
    // Keep an internal state for the currently displayed date
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    // When formStartDate changes, sync our selectedDate (one-way sync)
    useEffect(() => {
        if (formStartDate) {
            const parsed = new Date(formStartDate);
            if (!isNaN(parsed.getTime())) {
                setSelectedDate(parsed);
            }
        }
    }, [formStartDate]);

    // Calculate date ranges for the query
    const { dateFrom, dateTo } = useMemo(() => {
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(selectedDate);
        end.setHours(23, 59, 59, 999);
        return {
            dateFrom: toISOStringNoMs(start),
            dateTo: toISOStringNoMs(end),
        };
    }, [selectedDate]);

    const companyTz = useCompanyTimezone();

    // Check if selected date is today
    const isToday = useMemo(() => isTodayInTimeZone(selectedDate, companyTz), [selectedDate, companyTz]);

    const getDateDisplayText = () => {
        if (isToday) return 'Today';
        const now = getNowInCompanyTimeZone(companyTz);
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (
            selectedDate.getFullYear() === yesterday.getFullYear() &&
            selectedDate.getMonth() === yesterday.getMonth() &&
            selectedDate.getDate() === yesterday.getDate()
        ) {
            return 'Yesterday';
        }
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (
            selectedDate.getFullYear() === tomorrow.getFullYear() &&
            selectedDate.getMonth() === tomorrow.getMonth() &&
            selectedDate.getDate() === tomorrow.getDate()
        ) {
            return 'Tomorrow';
        }
        return selectedDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: companyTz || undefined,
        });
    };

    const handlePrevDay = () => setSelectedDate(prev => new Date(prev.getTime() - 86400000));
    const handleNextDay = () => setSelectedDate(prev => new Date(prev.getTime() + 86400000));
    const handleGoToToday = () => setSelectedDate(getNowInCompanyTimeZone(companyTz));

    // Fetch jobs
    const {
        data: jobsResponse,
        isLoading: jobsLoading,
    } = useQuery({
        queryKey: ['scheduler-assistant-jobs', dateFrom, dateTo, companyId],
        queryFn: () =>
            apiService.searchJobs({
                date_executed_from: dateFrom,
                date_executed_to: dateTo,
                is_canceled: false,
                exclude_completed: true,
                company_id: companyId, // Explicitly pass companyId
                page_size: 100,
            }),
        enabled: !!companyId, // Only run if company is selected
    });

    const jobs: JobListItemDTO[] = useMemo(() => {
        if (!jobsResponse) return [];
        let fetchedJobs = Array.isArray(jobsResponse) ? [...jobsResponse] : [...(jobsResponse.jobs || [])];
        
        // Ensure we only show jobs for the selected company, fallback if searchJobs doesn't filter exactly
        fetchedJobs = fetchedJobs.filter((j: any) => {
            if (j.company?.id) return j.company.id === companyId;
            return true; // if company structure is missing, assume it matches or let backend filter handle it
        });

        // Sort chronologically by start date
        fetchedJobs.sort((a: any, b: any) => {
            const timeA = a.date_executed_start ? new Date(a.date_executed_start).getTime() : 0;
            const timeB = b.date_executed_start ? new Date(b.date_executed_start).getTime() : 0;
            return timeA - timeB;
        });

        return fetchedJobs;
    }, [jobsResponse, companyId]);

    const formatTime = (iso?: string | null) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: companyTz || undefined });
    };

    if (!companyId) {
        return null;
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 mt-4 flex flex-col overflow-hidden flex-1 min-h-[100px]">
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 p-2 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <Calendar className="h-4 w-4 text-indigo-500" />
                    Schedule
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={handlePrevDay}
                        className="p-1 rounded hover:bg-gray-200 transition-colors shrink-0"
                        aria-label="Previous day"
                    >
                        <ChevronLeft className="h-3 w-3 text-gray-600" />
                    </button>
                    <div className="w-[100px] flex justify-center shrink-0">
                        <button
                            type="button"
                            onClick={handleGoToToday}
                            className={`text-xs font-medium px-2 py-0.5 rounded transition-colors w-full text-center ${
                                isToday ? 'text-primary-700 bg-primary-100' : 'text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {getDateDisplayText()}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={handleNextDay}
                        className="p-1 rounded hover:bg-gray-200 transition-colors shrink-0"
                        aria-label="Next day"
                    >
                        <ChevronRight className="h-3 w-3 text-gray-600" />
                    </button>
                </div>
            </div>

            {/* Job List */}
            <div className="p-2 overflow-y-auto overflow-x-hidden min-h-[100px] flex-1 custom-scrollbar">
                {jobsLoading ? (
                    <div className="flex justify-center items-center h-full py-6">
                        <LoadingSpinner />
                    </div>
                ) : jobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-8 text-center px-4">
                        <p className="text-sm text-gray-500 font-medium">No jobs scheduled</p>
                        <p className="text-xs text-gray-400 mt-1">for {isToday ? 'today' : 'this day'}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {jobs.map((job) => {
                            const startTime = formatTime(job.date_executed_start);
                            const endTime = formatTime(job.date_executed_end);
                            // ASAP jobs have no agreed time; their date is only a placeholder.
                            const timeRange = job.is_asap
                                ? 'ASAP'
                                : endTime && startTime !== endTime ? `${startTime} - ${endTime}` : startTime;
                            
                            const addressParts = job.address ? job.address.split(',').map(p => p.trim()) : [];
                            const shortAddress = addressParts.length >= 2 ? `${addressParts[0]}, ${addressParts[1]}` : (job.address || 'No address');

                            return (
                                <div
                                    key={job.id}
                                    className="relative group p-2 rounded-md border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    style={{ borderLeftColor: job.status_color || '#e5e7eb', borderLeftWidth: '3px' }}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs font-semibold text-gray-900">{timeRange || 'No time'}</span>
                                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-white border border-gray-200 whitespace-nowrap">
                                                    {job.job_type?.name || 'Job'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1 truncate">
                                                {shortAddress}
                                            </div>
                                        </div>
                                        
                                        <div className="text-xs text-gray-600 font-medium whitespace-nowrap shrink-0 text-right">
                                            {job.assigned_technician 
                                                ? job.assigned_technician.name.split(' ')[0] 
                                                : job.company?.name || 'Unassigned'}
                                        </div>
                                        
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                                            <Info className="h-3 w-3 text-gray-400" />
                                        </div>
                                    </div>
                                    
                                    {/* Tooltip on hover */}
                                    <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900 text-white text-xs rounded shadow-lg p-2.5 z-[100] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none origin-top-right">
                                        <div className="font-semibold mb-1 text-sm">{job.job_type?.name || 'Job'}</div>
                                        {job.assigned_technician && (
                                            <div className="text-gray-300 mb-0.5"><span className="text-gray-400">Tech:</span> {job.assigned_technician.name}</div>
                                        )}
                                        <div className="text-gray-300 mb-0.5"><span className="text-gray-400">Time:</span> {startTime} {endTime && startTime !== endTime ? `- ${endTime}` : ''}</div>
                                        <div className="text-gray-300 truncate"><span className="text-gray-400">Loc:</span> {shortAddress}</div>
                                        <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {/* Custom scrollbar styles would be in global css, but if not we still use normal scrollbar */}
        </div>
    );
}
